import { HttpClient } from '@angular/common/http';
import { Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { ShowPdfDialogComponent } from '@app/shared/components/show-pdf-dialog/show-pdf-dialog.component';
import { IVersion } from '@app/shared/models/version.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PdfService } from 'api/services';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom } from 'rxjs';
import { SelfserviceStore } from '@app/stores/selfserviceStore';
import { MatTooltip } from '@angular/material/tooltip';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-global-footer',
    templateUrl: './global-footer.component.html',
    styleUrls: ['./global-footer.component.scss'],
    imports: [MatTooltip, TranslateModule],
})
export class GlobalFooterComponent implements OnInit {
    private selfserviceStore = inject(SelfserviceStore);
    versionString = signal('');
    footerVisibility = signal(true);
    footerVisibilityInput = input<boolean>(undefined, {
        alias: 'footerVisibility',
    });
    isSelfService = input<boolean>(false);

    footerLinks = [
        {
            translationKey: 'GENERAL.IMPRINT',
            link: 'https://www.gs1-germany.de/impressum/',
        },
        {
            translationKey: 'GENERAL.PRIVACY_POLICY',
            link: 'https://www.gs1-germany.de/datenschutz/',
        },
        {
            translationKey: 'GENERAL.AGB',
            link: 'https://orders.gs1-germany.de/fileadmin/gs1/agb/Cloud4Log_AGB_inkl_Anlagen_01.01.2026.pdf',
        },
        {
            translationKey: 'GENERAL.GS1',
            link: 'https://www.gs1-germany.de/',
        },
    ];

    selfServiceFooterLinks = [
        {
            translationKey: 'SELFSERVICE.FOOTER.LEGAL',
        },
    ];

    constructor(
        private router: Router,
        private http: HttpClient,
        private spinner: NgxSpinnerService,
        private pdfService: PdfService,
        private dialog: MatDialog,
        private translateService: TranslateService
    ) {
        effect(() => {
            const value = this.footerVisibilityInput();
            if (value !== undefined) {
                this.footerVisibility.set(value);
            }
        });
    }

    async ngOnInit() {
        this.observeRoutes();
        try {
            const version = await lastValueFrom(
                this.http.get<IVersion>(
                    `assets/version.json?v=${new Date().getTime()}`
                )
            );
            if (version) {
                let versionString = '';
                if (version.release) {
                    versionString += `Release:\n${version.release}\n`;
                }
                if (version.commit) {
                    versionString += `Commit ID:\n${version.commit}`;
                }
                this.versionString.set(versionString);
            }
        } catch (error) {}
    }

    private observeRoutes(): void {
        this.router.events.pipe(untilDestroyed(this)).subscribe(event => {
            if (event instanceof NavigationEnd) {
                const showNoHeader = [
                    '/login-callback',
                    '/forgot-password',
                    '/check-email',
                    '/set-new-password',
                ];
                this.footerVisibility.set(!showNoHeader.includes(
                    event.urlAfterRedirects
                ));
            }
        });
    }

    /**
     * @param legalNote
     * opens modal dialog with pdf preview
     */
    async viewLegalNote() {
        let blobData: Blob | undefined;
        await this.spinner.show();
        try {
            const organizationSite =
                this.selfserviceStore.organizationSite();
            const organizationSiteKey = organizationSite?._key;
            if (!organizationSiteKey) {
                throw new Error('Organization site key is not available');
            }

            blobData = await lastValueFrom(
                this.pdfService.getConsigneeSelfCheckinLegalDocument({
                    organizationSiteKey: organizationSiteKey,
                })
            );

            await this.spinner.hide();

            this.dialog.open(ShowPdfDialogComponent, {
                minWidth: '80%',
                height: '95%',
                disableClose: true,
                data: {
                    data: blobData,
                },
            });
        } catch (error) {
            this.spinner.hide();
            this.translateService.instant('ERROR.DOWNLOAD-NOTE');
        }
    }
}
